import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { AuthorizationDialog } from '@/components/ui/authorization-dialog';
import { Plus, X, Trash2, Pencil, Eye, Printer, Search, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useGlobalBarcodeScanner } from '@/hooks/useGlobalBarcodeScanner';
import { usePermissions } from '@/hooks/use-permissions';
import { useAutoRefresh } from '@/hooks/use-auto-refresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function TrimsData({ user }) {
  const navigate = useNavigate();
  const { canCreate, canEdit, canDelete } = usePermissions(user);
  const [trims, setTrims] = useState([]);
  const [filteredTrims, setFilteredTrims] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [itemNameFilter, setItemNameFilter] = useState('');
  const [materialFilter, setMaterialFilter] = useState('');
  const [colorNameFilter, setColorNameFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [vendorCodeFilter, setVendorCodeFilter] = useState('');
  const [open, setOpen] = useState(false);
  const [editTrim, setEditTrim] = useState(null);
  const [viewDetails, setViewDetails] = useState(null);
  const [colorSearchQuery, setColorSearchQuery] = useState('');
  const [printBarcode, setPrintBarcode] = useState(null);
  const [printSize, setPrintSize] = useState({ width: 50, height: 30 });
  const [formData, setFormData] = useState({
    ln_z_code: 'LN-Z-', vendor_name: '', vendor_code: '', item_name: '', size_type: '', diameter: '', height: '', width: '', material: '', colors: []
  });
  const [vendorDetailsModal, setVendorDetailsModal] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [trimToDelete, setTrimToDelete] = useState(null);
  const [deleteColorDialogOpen, setDeleteColorDialogOpen] = useState(false);
  const [colorToDelete, setColorToDelete] = useState(null);
  const [imagePopup, setImagePopup] = useState(null); // For full-size image view

  // Global barcode scanner for Trims
  const handleGlobalBarcodeScanned = (scannedCode) => {
    console.log('🔍 Trims page received barcode:', scannedCode);
    
    const found = trims.find(trim => {
      if (trim.ln_z_code.toLowerCase() === scannedCode.toLowerCase()) {
        return true;
      }
      
      if (trim.colors?.some(color => {
        const colorBarcodeCode = `${trim.ln_z_code}-${color.color_code}`.toLowerCase();
        return colorBarcodeCode === scannedCode.toLowerCase();
      })) {
        return true;
      }
      
      return false;
    });
    
    if (found) {
      setViewDetails(found);
      const isColorCode = scannedCode.includes('-') && scannedCode.split('-').length > 3;
      if (isColorCode) {
        const colorCode = scannedCode.split('-').pop();
        setColorSearchQuery(colorCode);
      } else {
        setColorSearchQuery('');
      }
      toast.success(`✅ Trims found: ${found.ln_z_code}`);
    } else {
      toast.error(`❌ No trims found with barcode: ${scannedCode}`);
    }
  };

  useGlobalBarcodeScanner(handleGlobalBarcodeScanned);

  useEffect(() => { fetchTrims(); fetchVendors(); }, []);

  useEffect(() => {
    let filtered = trims;

    // Filter by LN-Z Code or Color Code (for barcode scanning)
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(trim => {
        // Check main LN-Z code
        if (trim.ln_z_code.toLowerCase().includes(query)) {
          return true;
        }
        
        // Check if search query matches any color barcode (e.g., "LN-Z-00045-003")
        if (trim.colors?.some(color => {
          const colorBarcodeCode = `${trim.ln_z_code}-${color.color_code}`.toLowerCase();
          return colorBarcodeCode === query || colorBarcodeCode.includes(query);
        })) {
          return true;
        }
        
        // Check if search query is exact match for main barcode
        if (trim.ln_z_code.toLowerCase() === query) {
          return true;
        }
        
        return false;
      });
    }

    // Filter by Item Name
    if (itemNameFilter.trim() !== '') {
      const query = itemNameFilter.toLowerCase();
      filtered = filtered.filter(trim => 
        trim.item_name.toLowerCase().includes(query)
      );
    }

    // Filter by Material
    if (materialFilter.trim() !== '') {
      const query = materialFilter.toLowerCase();
      filtered = filtered.filter(trim => 
        trim.material.toLowerCase().includes(query)
      );
    }

    // Filter by Color Name
    if (colorNameFilter.trim() !== '') {
      const colorQuery = colorNameFilter.toLowerCase();
      filtered = filtered.filter(trim => 
        trim.colors?.some(color => 
          color.color_name.toLowerCase().includes(colorQuery)
        )
      );
    }

    // Filter by Supplier
    if (supplierFilter.trim() !== '') {
      filtered = filtered.filter(trim => 
        trim.vendor_name && trim.vendor_name.toLowerCase().includes(supplierFilter.toLowerCase())
      );
    }

    // Filter by Vendor Code
    if (vendorCodeFilter.trim() !== '') {
      filtered = filtered.filter(trim => 
        trim.vendor_code && trim.vendor_code.toLowerCase().includes(vendorCodeFilter.toLowerCase())
      );
    }

    setFilteredTrims(filtered);
  }, [searchQuery, itemNameFilter, materialFilter, colorNameFilter, supplierFilter, vendorCodeFilter, trims]);

  const extractNumeric = (code) => {
    return code.replace(/\D/g, '').replace(/^0+/, '');
  };

  const handleBarcodeSearch = (e) => {
    if (e && e.key === 'Enter' && searchQuery.trim() !== '') {
      e.preventDefault();
      const query = searchQuery.toLowerCase().trim();
      
      console.log('🔍 Searching trims for:', query);
      
      // Find trims by main code or color code
      const found = trims.find(trim => {
        // Check exact match for main LN-Z code
        if (trim.ln_z_code.toLowerCase() === query) {
          return true;
        }
        
        // Check partial match for main LN-Z code
        if (trim.ln_z_code.toLowerCase().includes(query)) {
          return true;
        }
        
        // Check if query matches any color barcode (e.g., "LN-Z-00045-003")
        if (trim.colors?.some(color => {
          const colorBarcodeCode = `${trim.ln_z_code}-${color.color_code}`.toLowerCase();
          return colorBarcodeCode === query || colorBarcodeCode.includes(query);
        })) {
          return true;
        }
        
        return false;
      });
      
      if (found) {
        console.log('✅ Found trims:', found.ln_z_code);
        setViewDetails(found);
        
        // If it's a color code search, highlight that color
        const colorMatch = query.includes('-') && query.split('-').length > 3;
        if (colorMatch) {
          const colorCode = query.split('-').pop();
          setColorSearchQuery(colorCode);
        } else {
          setColorSearchQuery('');
        }
        
        toast.success(`✅ Found: ${found.ln_z_code}`);
      } else {
        console.log('❌ No trims found for:', query);
        toast.error(`❌ No trims found: ${query}`);
      }
    }
  };

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vendors`, { headers: { Authorization: `Bearer ${token}` } });
      setVendors(response.data);
    } catch (error) {
      console.error('Failed to fetch vendors');
    }
  };

  const handleVendorClick = (vendorName) => {
    const vendor = vendors.find(v => v.vendor_name === vendorName);
    if (vendor) {
      setVendorDetailsModal(vendor);
    }
  };

  const fetchTrims = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/trims`, { headers: { Authorization: `Bearer ${token}` } });
      setTrims(response.data);
      setFilteredTrims(response.data);
    } catch (error) {
      toast.error('Failed to fetch trims');
    }
  };

  // Auto-refresh every 5 seconds, but pause when user is editing
  const isUserEditing = open || !!editTrim;
  useAutoRefresh(fetchTrims, 5, isUserEditing);

  // EXACT Digital Design pattern - Direct array manipulation
  const handleAddColor = () => {
    setFormData({
      ...formData,
      colors: [...formData.colors, { color_code: '', color_name: '', price: '', image: '' }]
    });
  };

  const handleColorChange = (index, field, value) => {
    const updatedColors = [...formData.colors];
    updatedColors[index][field] = value;
    setFormData({ ...formData, colors: updatedColors });
  };

  const handleColorImageUpload = (index, file) => {
    if (!file) return;
    
    const reader = new FileReader();
    reader.onloadend = () => {
      const updatedColors = [...formData.colors];
      updatedColors[index].image = reader.result;
      setFormData({ ...formData, colors: updatedColors });
    };
    reader.readAsDataURL(file);
  };

  const removeColor = (index) => {
    handleRemoveColor(index);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      // Clean up color data - convert empty/invalid values to null, with NaN protection
      const cleanedData = {
        ...formData,
        colors: formData.colors.map(color => {
          const priceValue = (color.price && color.price !== '' && color.price !== '0') ? parseFloat(color.price) : null;
          
          return {
            ...color,
            price: (priceValue !== null && !isNaN(priceValue)) ? priceValue : null
          };
        })
      };
      
      if (editTrim) {
        await axios.put(`${API}/trims/${editTrim.id}`, cleanedData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Updated successfully');
      } else {
        await axios.post(`${API}/trims`, cleanedData, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Created successfully');
      }
      setOpen(false);
      setEditTrim(null);
      setFormData({ ln_z_code: 'LN-Z-', vendor_name: '', vendor_code: '', item_name: '', size_type: '', diameter: '', height: '', width: '', material: '', colors: [] });
      fetchTrims();
    } catch (error) {
      console.error('Trims save error:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Operation failed';
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (trim) => {
    setTrimToDelete(trim);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!trimToDelete) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/trims/${trimToDelete.id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted successfully');
      setDeleteDialogOpen(false);
      setTrimToDelete(null);
      fetchTrims();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setTrimToDelete(null);
  };

  const handleRemoveColor = (index) => {
    setColorToDelete(index);
    setDeleteColorDialogOpen(true);
  };

  const confirmColorDelete = () => {
    if (colorToDelete === null) return;
    const updatedColors = formData.colors.filter((_, i) => i !== colorToDelete);
    setFormData({ ...formData, colors: updatedColors });
    setDeleteColorDialogOpen(false);
    setColorToDelete(null);
    toast.success('Color removed');
  };

  const cancelColorDelete = () => {
    setDeleteColorDialogOpen(false);
    setColorToDelete(null);
  };

    const handlePrint = () => {
    if (!printBarcode) return;
    const printWindow = window.open('', '_blank');
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Barcode - ${printBarcode.code}</title>
          <style>
            @page { 
              size: ${printSize.width}mm ${printSize.height}mm;
              margin: 0;
            }
            @media print {
              body { margin: 0; padding: 0; }
            }
            body { 
              margin: 0; 
              padding: 0; 
              display: flex; 
              justify-content: center; 
              align-items: center; 
              width: ${printSize.width}mm;
              height: ${printSize.height}mm;
            }
            .barcode-container { 
              text-align: center;
              page-break-inside: avoid;
              width: 100%;
              height: 100%;
              display: flex;
              justify-content: center;
              align-items: center;
            }
            img { 
              max-width: 100%;
              max-height: 100%;
              display: block;
            }
          </style>
        </head>
        <body>
          <div class="barcode-container">
            <img src="${printBarcode.image}" alt="Barcode" onload="window.print(); window.onafterprint = function() { window.close(); }" />
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Trims & Accessories (LN-Z-CODE)</h1>
              <p className="text-sm text-gray-600">Manage trims and accessories</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6 space-y-4">
        {/* Main Search Bar */}
        <div className="flex gap-4 items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input 
              placeholder="Search or scan barcode" 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)} 
              onKeyDown={handleBarcodeSearch} 
              className="pl-10"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              tabIndex="1"
              data-barcode-input="true"
            />
          </div>
          {canCreate('master_data') && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button onClick={async () => { 
                  setEditTrim(null); 
                  try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${API}/master-data/next-code/trim`, {
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    if (response.ok) {
                      const data = await response.json();
                      setFormData({ ln_z_code: data.next_code, vendor_name: '', vendor_code: '', item_name: '', size_type: '', diameter: '', height: '', width: '', material: '', colors: [] });
                    } else {
                      setFormData({ ln_z_code: 'LN-Z-00001', vendor_name: '', vendor_code: '', item_name: '', size_type: '', diameter: '', height: '', width: '', material: '', colors: [] });
                    }
                  } catch (error) {
                    console.error('Error fetching next code:', error);
                    setFormData({ ln_z_code: 'LN-Z-00001', vendor_name: '', vendor_code: '', item_name: '', size_type: '', diameter: '', height: '', width: '', material: '', colors: [] });
                  }
                }}>
                  <Plus className="w-4 h-4 mr-2" /> Add Trims and Accessories
                </Button>
              </DialogTrigger>
            </Dialog>
          )}
        </div>

        {/* Filters Section */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Item Name</Label>
            <Input
              placeholder="Search by item name"
              value={itemNameFilter}
              onChange={(e) => setItemNameFilter(e.target.value)}
              className="text-sm"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-sm font-medium">Material</Label>
            <Input
              placeholder="Search by material"
              value={materialFilter}
              onChange={(e) => setMaterialFilter(e.target.value)}
              className="text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Color Name</Label>
            <Input
              placeholder="Search by color name"
              value={colorNameFilter}
              onChange={(e) => setColorNameFilter(e.target.value)}
              className="text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Supplier</Label>
            <SearchableSelect
              options={[
                { value: '', label: 'All Suppliers' },
                ...Array.from(new Set(trims.map(t => t.vendor_name).filter(Boolean)))
                  .sort()
                  .map(supplier => ({ value: supplier, label: supplier }))
              ]}
              value={supplierFilter}
              onChange={setSupplierFilter}
              placeholder="Select supplier..."
              className="text-sm"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium">Vendor Code</Label>
            <Input
              type="text"
              value={vendorCodeFilter}
              onChange={(e) => setVendorCodeFilter(e.target.value)}
              placeholder="Type vendor code..."
              className="text-sm"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-sm font-medium">Actions</Label>
            <Button 
              variant="outline" 
              onClick={() => {
                setSearchQuery('');
                setItemNameFilter('');
                setMaterialFilter('');
                setColorNameFilter('');
                setSupplierFilter('');
                setVendorCodeFilter('');
              }}
              className="w-full text-sm"
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) setErrorMessage(''); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto max-w-4xl">
            <DialogHeader><DialogTitle>{editTrim ? 'Edit Trims and Accessories' : 'Add New Trims and Accessories (LN-Z-CODE)'}</DialogTitle></DialogHeader>
            {errorMessage && (
              <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div className="ml-3 flex-1">
                    <h3 className="text-sm font-bold text-red-800 mb-1">Error</h3>
                    <p className="text-sm text-red-700">{errorMessage}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setErrorMessage('')}
                    className="ml-auto flex-shrink-0 inline-flex text-red-400 hover:text-red-600"
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label><span className="text-red-500">*</span> LN-Z Code</Label>
                <Input 
                  value={formData.ln_z_code} 
                  onChange={(e) => {
                    setFormData({ ...formData, ln_z_code: e.target.value });
                  }}
                  required 
                  placeholder="LN-Z-00001" 
                />
                <p className="text-xs text-gray-500">Auto-generated, but you can edit it</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label><span className="text-red-500">*</span> Vendor</Label>
                  <SearchableSelect
                    options={[
                      { value: "", label: "Select Vendor" },
                      ...vendors.map(v => ({ value: v.vendor_name, label: v.vendor_name }))
                    ]}
                    value={formData.vendor_name}
                    onChange={(value) => setFormData({ ...formData, vendor_name: value })}
                    placeholder="Select Vendor"
                    searchPlaceholder="Search vendor..."
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Vendor Code</Label>
                  <Input value={formData.vendor_code} onChange={(e) => setFormData({ ...formData, vendor_code: e.target.value })} placeholder="Enter vendor code" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label><span className="text-red-500">*</span> Item Name</Label>
                  <Input value={formData.item_name} onChange={(e) => setFormData({ ...formData, item_name: e.target.value })} required placeholder="Enter item name" />
                </div>
                <div className="space-y-2">
                  <Label><span className="text-red-500">*</span> Material</Label>
                  <Input value={formData.material} onChange={(e) => setFormData({ ...formData, material: e.target.value })} required placeholder="Enter material type" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label><span className="text-red-500">*</span> Size Type</Label>
                  <SearchableSelect 
                    options={[
                      { value: "", label: "Select Size Type" },
                      { value: "DIAMETER", label: "Diameter" },
                      { value: "H/W", label: "Height / Width" }
                    ]}
                    value={formData.size_type} 
                    onChange={(value) => setFormData({ ...formData, size_type: value, diameter: '', height: '', width: '' })} 
                    placeholder="Select Size Type"
                    searchPlaceholder="Search size type..."
                    required
                  />
                </div>
                {formData.size_type === 'DIAMETER' && (
                  <div className="space-y-2">
                    <Label><span className="text-red-500">*</span> Diameter (CM)</Label>
                    <Input 
                      type="number" 
                      step="0.01"
                      value={formData.diameter} 
                      onChange={(e) => setFormData({ ...formData, diameter: e.target.value })} 
                      required 
                      placeholder="Enter diameter in CM" 
                    />
                  </div>
                )}
                {formData.size_type === 'H/W' && (
                  <>
                    <div className="space-y-2">
                      <Label><span className="text-red-500">*</span> Height (CM)</Label>
                      <Input 
                        type="number" 
                        step="0.01"
                        value={formData.height} 
                        onChange={(e) => setFormData({ ...formData, height: e.target.value })} 
                        required 
                        placeholder="Enter height in CM" 
                      />
                    </div>
                    <div className="space-y-2">
                      <Label><span className="text-red-500">*</span> Width (CM)</Label>
                      <Input 
                        type="number" 
                        step="0.01"
                        value={formData.width} 
                        onChange={(e) => setFormData({ ...formData, width: e.target.value })} 
                        required 
                        placeholder="Enter width in CM" 
                      />
                    </div>
                  </>
                )}
              </div>
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold">Colors</h3>
                  <Button type="button" onClick={handleAddColor} size="sm">
                    <Plus className="w-4 h-4 mr-1" /> Add Color
                  </Button>
                </div>
                {formData.colors.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No colors added yet</p>
                ) : (
                  <div className="space-y-3">
                    {formData.colors.map((color, index) => (
                      <div key={index} className="border border-gray-300 rounded-lg p-3 space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium">Color {index + 1}</span>
                          <Button 
                            type="button" 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => removeColor(index)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>

                        <div className="grid grid-cols-3 gap-3">
                          <div className="space-y-1">
                            <Label className="text-xs">Color Code</Label>
                            <Input
                              value={color.color_code}
                              onChange={(e) => handleColorChange(index, 'color_code', e.target.value)}
                              placeholder="Enter color code"
                              className="text-sm"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Color Name</Label>
                            <Input
                              value={color.color_name}
                              onChange={(e) => handleColorChange(index, 'color_name', e.target.value)}
                              placeholder="Enter color name"
                              className="text-sm"
                            />
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Price (USD)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={color.price}
                              onChange={(e) => handleColorChange(index, 'price', e.target.value)}
                              placeholder="Enter price"
                              className="text-sm"
                            />
                          </div>
                        </div>

                        <div className="space-y-1">
                          <Label className="text-xs">Color Image</Label>
                          <Input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleColorImageUpload(index, e.target.files[0])}
                            className="text-sm border-0 p-0 overflow-visible file:mr-4 file:py-1.5 file:px-3 file:rounded file:border-2 file:border-black file:text-xs file:font-semibold file:bg-white file:text-black hover:file:bg-gray-100 file:cursor-pointer"
                          />
                          {(color.image_path || color.image) && (
                            <div className="mt-2">
                              <img 
                                src={color.image_path ? `${API}/${color.image_path}` : color.image} 
                                alt={`Color ${index + 1}`} 
                                className="w-20 h-20 object-cover rounded border" 
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <Button type="submit" className="w-full">{editTrim ? 'Update' : 'Create'} Trims and Accessories</Button>
            </form>
          </DialogContent>
        </Dialog>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {filteredTrims.map((trim) => (
          <div key={trim.id} className="bg-white border-2 border-black rounded-lg shadow p-4 flex flex-col items-center justify-between hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => setViewDetails(trim)}>
            <h3 className="text-lg font-bold text-center mb-2">{trim.ln_z_code}</h3>
            <p className="text-xs text-gray-500 text-center mb-3">{trim.colors?.length || 0} colors</p>
            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
              {canEdit('master_data') && (
                <Button variant="ghost" size="icon" onClick={() => { setEditTrim(trim); setFormData(trim); setOpen(true); }}>
                  <Pencil className="w-4 h-4" />
                </Button>
              )}
              {canDelete('master_data') && (
                <Button variant="ghost" size="icon" onClick={() => handleDelete(trim)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredTrims.length === 0 && (
        <div className="text-center py-12"><p className="text-gray-500">No trims and accessories found.</p></div>
      )}

      <Dialog open={!!viewDetails} onOpenChange={() => { setViewDetails(null); setColorSearchQuery(''); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Trims and Accessories Details - {viewDetails?.ln_z_code}</DialogTitle></DialogHeader>
          {viewDetails && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><span className="font-semibold">LN-Z Code:</span> {viewDetails.ln_z_code}</div>
                {viewDetails.vendor_name && (
                  <div className="col-span-2 flex items-center gap-2">
                    <span className="font-semibold">Vendor:</span>
                    <span 
                      onClick={() => handleVendorClick(viewDetails.vendor_name)}
                      className="cursor-pointer hover:underline text-blue-600"
                    >
                      {viewDetails.vendor_name}
                    </span>
                    {(() => {
                      const vendor = vendors.find(v => v.vendor_name === viewDetails.vendor_name);
                      return vendor && (
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          vendor.local_or_imported === 'Local' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                        }`}>
                          {vendor.local_or_imported}-{vendor.country}
                        </span>
                      );
                    })()}
                  </div>
                )}
                {viewDetails.vendor_code && (
                  <div><span className="font-semibold">Vendor Code:</span> {viewDetails.vendor_code}</div>
                )}
                <div><span className="font-semibold">Item Name:</span> {viewDetails.item_name}</div>
                <div>
                  <span className="font-semibold">Size:</span> {
                    viewDetails.size_type === 'DIAMETER' 
                      ? `${viewDetails.diameter} CM (Diameter)` 
                      : viewDetails.size_type === 'H/W'
                      ? `H: ${viewDetails.height} CM, W: ${viewDetails.width} CM`
                      : viewDetails.size_cm ? `${viewDetails.size_cm} CM` : 'N/A'
                  }
                </div>
                <div><span className="font-semibold">Material:</span> {viewDetails.material}</div>
              </div>
              
              {/* Main Trims Barcode Section */}
              {viewDetails.barcode_path && (
                <div className="border-t pt-4 pb-4 border-b">
                  <div className="space-y-2 text-center">
                    <Button variant="outline" size="sm" onClick={() => setPrintBarcode({ 
                      image: `${API}/${viewDetails.barcode_path}`, 
                      code: viewDetails.ln_z_code 
                    })}>
                      <Printer className="w-3 h-3 mr-1" /> Print Main Barcode
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold">Colors ({viewDetails.colors?.filter(color => {
                    if (!colorSearchQuery.trim()) return true;
                    
                    let query = colorSearchQuery.trim();
                    
                    // If it's a full barcode (e.g., "LN-Z-00095-001"), extract just the color part
                    if (query.includes('-') && query.split('-').length >= 4) { // LN-Z-code-color has 4 parts
                      query = query.split('-').pop(); // Get the last part (color code)
                    }
                    
                    // Clean the query and color code for comparison
                    query = query.replace(/\D/g, '').replace(/^0+/, '') || query;
                    const colorCode = color.color_code.replace(/\D/g, '').replace(/^0+/, '') || color.color_code;
                    
                    // Match by color code, color name, or barcode
                    return colorCode.includes(query) || 
                           color.color_code.toLowerCase().includes(colorSearchQuery.toLowerCase()) ||
                           color.color_name.toLowerCase().includes(colorSearchQuery.toLowerCase());
                  }).length || 0})</h4>
                  <div className="relative flex-1 max-w-xs ml-4">
                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
                    <Input 
                      placeholder="Search by code, name, or barcode" 
                      value={colorSearchQuery}
                      onChange={(e) => setColorSearchQuery(e.target.value)}
                      className="pl-10"
                      autoComplete="off"
                      autoCorrect="off"
                      autoCapitalize="off"
                      spellCheck="false"
                      tabIndex="2"
                      data-barcode-input="true"
                    />
                  </div>
                </div>
                <div className="space-y-3">
                  {viewDetails.colors?.filter(color => {
                    if (!colorSearchQuery.trim()) return true;
                    
                    let query = colorSearchQuery.trim();
                    
                    // If it's a full barcode (e.g., "LN-Z-00095-001"), extract just the color part
                    if (query.includes('-') && query.split('-').length >= 4) { // LN-Z-code-color has 4 parts
                      query = query.split('-').pop(); // Get the last part (color code)
                    }
                    
                    // Clean the query and color code for comparison
                    query = query.replace(/\D/g, '').replace(/^0+/, '') || query;
                    const colorCode = color.color_code.replace(/\D/g, '').replace(/^0+/, '') || color.color_code;
                    
                    // Match by color code, color name, or barcode
                    return colorCode.includes(query) || 
                           color.color_code.toLowerCase().includes(colorSearchQuery.toLowerCase()) ||
                           color.color_name.toLowerCase().includes(colorSearchQuery.toLowerCase());
                  }).map((color, idx) => (
                    <div key={idx} className="border rounded p-4 bg-gray-50">
                      <div className="mb-3">
                        <div className="mb-2">
                          <span className="font-medium">Code:</span> {color.barcode?.code || `${viewDetails.ln_z_code}-${color.color_code}`}
                        </div>
                        <div className="mb-2">
                          <span className="font-medium">Name:</span> {color.color_name}
                        </div>
                        <div className="mb-2">
                          <span className="font-medium">Price:</span> ${color.price} USD
                        </div>
                      </div>
                      
                      {(color.image_path || color.image) && (
                        <div className="mb-3">
                          <span className="font-medium block mb-2">Image:</span>
                          <div className="flex justify-center">
                            <img 
                              src={color.image_path ? `${API}/${color.image_path}` : color.image} 
                              alt={color.color_name} 
                              className="max-w-xs max-h-64 object-contain rounded border cursor-pointer hover:opacity-80 transition-opacity" 
                              onClick={() => setImagePopup({ image: color.image_path ? `${API}/${color.image_path}` : color.image, name: color.color_name })}
                              title="Click to enlarge"
                            />
                          </div>
                        </div>
                      )}
                      
                      {color.barcode_path && (
                        <div className="space-y-2 mt-3">
                          <Button variant="outline" size="sm" className="w-full" onClick={() => setPrintBarcode({ 
                            image: `${API}/${color.barcode_path}`, 
                            code: `${viewDetails.ln_z_code}-${color.color_code}` 
                          })}>
                            <Printer className="w-3 h-3 mr-1" /> Print Barcode
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!printBarcode} onOpenChange={() => setPrintBarcode(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Print Barcode - {printBarcode?.code}</DialogTitle></DialogHeader>
          {printBarcode && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <img src={printBarcode.image} alt="Barcode" className="max-w-full" />
              </div>
              <div className="space-y-3">
                <Label>Print Size (mm)</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Width</Label>
                    <Input type="number" value={printSize.width} onChange={(e) => setPrintSize({ ...printSize, width: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-sm">Height</Label>
                    <Input type="number" value={printSize.height} onChange={(e) => setPrintSize({ ...printSize, height: e.target.value })} />
                  </div>
                </div>
              </div>
              <Button onClick={handlePrint} className="w-full">
                <Printer className="w-4 h-4 mr-2" /> Print Barcode
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Vendor Details Popup Modal */}
      <Dialog open={!!vendorDetailsModal} onOpenChange={() => setVendorDetailsModal(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Vendor Details - {vendorDetailsModal?.vendor_name}</DialogTitle>
          </DialogHeader>
          {vendorDetailsModal && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><span className="font-semibold">Name:</span> {vendorDetailsModal.vendor_name}</div>
                <div>
                  <span className="font-semibold">Type: </span>
                  <span className={`px-2 py-1 rounded text-sm font-medium ${
                    vendorDetailsModal.local_or_imported === 'Local' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {vendorDetailsModal.local_or_imported}
                  </span>
                </div>
                <div><span className="font-semibold">Vendor Type:</span> {vendorDetailsModal.vendor_type || 'N/A'}</div>
                <div><span className="font-semibold">Phone:</span> {vendorDetailsModal.phone_number || 'N/A'}</div>
                <div><span className="font-semibold">Alt Number:</span> {vendorDetailsModal.alt_number || 'N/A'}</div>
                <div><span className="font-semibold">City:</span> {vendorDetailsModal.city}</div>
                <div><span className="font-semibold">Country:</span> {vendorDetailsModal.country}</div>
                <div className="col-span-2"><span className="font-semibold">Address:</span> {vendorDetailsModal.address || 'N/A'}</div>
              </div>
              {vendorDetailsModal.wechat_qr_code && (
                <div className="mt-4 pt-4 border-t">
                  <p className="font-semibold mb-2">WeChat QR Code:</p>
                  <img 
                    src={vendorDetailsModal.wechat_qr_code} 
                    alt="WeChat QR Code" 
                    className="w-48 h-48 object-contain border rounded p-2"
                  />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Full-Size Image Popup */}
      <Dialog open={!!imagePopup} onOpenChange={() => setImagePopup(null)}>
        <DialogContent className="max-w-6xl max-h-[90vh] p-0">
          <DialogHeader className="p-4 border-b">
            <DialogTitle>Image Preview - {imagePopup?.name}</DialogTitle>
          </DialogHeader>
          {imagePopup && (
            <div className="p-4 overflow-auto flex items-center justify-center" style={{ maxHeight: 'calc(90vh - 80px)' }}>
              <img 
                src={imagePopup.image} 
                alt={imagePopup.name} 
                className="max-w-full h-auto object-contain"
                style={{ cursor: 'zoom-in' }}
                onClick={(e) => {
                  if (e.target.style.transform === 'scale(2)') {
                    e.target.style.transform = 'scale(1)';
                    e.target.style.cursor = 'zoom-in';
                  } else {
                    e.target.style.transform = 'scale(2)';
                    e.target.style.cursor = 'zoom-out';
                  }
                }}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AuthorizationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onAuthorized={confirmDelete}
        title="Delete Trim"
        message={trimToDelete ? `Enter authorization code to delete trim ${trimToDelete.ln_z_code}` : 'Enter authorization code to proceed'}
      />

      {/* Delete Color Authorization Dialog */}
      <AuthorizationDialog
        open={deleteColorDialogOpen}
        onOpenChange={setDeleteColorDialogOpen}
        onAuthorized={confirmColorDelete}
        title="Remove Color"
        message={colorToDelete !== null && formData.colors[colorToDelete] ? `Enter authorization code to remove color ${formData.colors[colorToDelete].color_name}` : 'Enter authorization code to proceed'}
      />
      </main>
    </div>
  );
}